<template>
  <div class="loan-summary">
    <div class="profile-section">
      <div class="profile-picture">
        <span>{{ primaryBorrower?.initials || "?" }}</span>
      </div>
    </div>
    <div class="column">
      <h2>{{ primaryBorrower?.fullName || "Unknown" }}</h2>
      <p
        v-if="loanDetails.isParentLOC"
        class="icon-student student-info"
        id="student"
      >
        <span
          :title="
            loanDetails.student?.fullName
              ? loanDetails.student?.fullName
              : undefined
          "
        >
          &nbsp;{{ loanDetails.student?.fullName }}</span
        >
      </p>
      <p class="subtext">{{ loanDetails.lenderName }}</p>
      <p class="subtext">{{ loanDetails.programName }}</p>
      <span class="status">{{ loanDetails.loanStatusHumanized }}</span>
    </div>

    <div class="column">
      <h3>Total Disbursed Amount</h3>
      <p class="value">{{ loanDetails.disbursedAmountFormatted }}</p>
      <p class="subtext">
        {{ loanDetails.loanAmountTitle }}:
        {{ loanDetails.loanAmountFormatted }}
      </p>
    </div>

    <div class="column">
      <h3>Application Interest Rate</h3>
      <p class="value">{{ loanDetails.applicationInterestRateFormatted }}</p>
      <p class="subtext">{{ loanDetails.rateTypeHumanized }}</p>
    </div>

    <div v-if="loanDetails.locLimitLoanAmount" class="column">
      <h3>{{ loanDetails.locLimitLoanAmount.label }}</h3>
      <p class="value">{{ loanDetails.locLimitLoanAmount.displayValue }}</p>
    </div>
  </div>

  <div class="details-wrapper">
    <button @click="toggleDetails" class="toggle-button">
      <span :class="detailsVisible ? 'arrow-up' : 'arrow-down'"></span>
    </button>

    <SlideTransition :is-visible="detailsVisible">
      <Details :referenceId="referenceId" :loanDetails="loanDetails" />
    </SlideTransition>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import SlideTransition from "@/components/shared/SlideTransition.vue";
import Details from "@/components/loan/Details.vue";
import { LoanDetails } from "@/models/loans";

const props = defineProps<{
  referenceId: string | undefined;
  loanDetails: LoanDetails;
}>();

const detailsVisible = ref(false);

const toggleDetails = () => {
  detailsVisible.value = !detailsVisible.value;
};

const primaryBorrower = computed(() => {
  const borrowers = props.loanDetails.borrowers || [];
  if (borrowers && borrowers.length > 0) {
    return borrowers[0];
  }
  return null;
});
</script>

<style scoped>
.loan-summary {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  padding: 16px 20px;
  gap: 16px;
  background-color: #e2e4e9;
  border-bottom: solid 1px #ccc;
}

.profile-picture {
  width: 108px;
  height: 108px;
  font-size: 36px;
  border-radius: 50%;
  background-color: black;
  color: white;
  display: flex;
  justify-content: center;
  align-items: center;
}

.subtext {
  color: #777;
  font-size: 13px;
  margin: 4px 0;
}

.status {
  display: inline-block;
  padding: 4px 8px;
  font-size: 12px;
  font-weight: bold;
  background-color: white;
  margin-top: 4px;
  border-radius: 20px;
}

.column {
  flex: 1;
  min-width: 196px;
}

h2 {
  font-size: 18px;
  margin: 4px 0;
  text-wrap: nowrap;
}

h3 {
  font-size: 16px;
  color: #333;
  margin-bottom: 4px;
  text-wrap: nowrap;
}

.value {
  font-size: 20px;
  font-weight: bold;
  color: black;
  margin: 0;
}

.details-wrapper {
  position: relative;
  padding-top: 24px;
  border-bottom: solid 1px #ccc;
}

.toggle-button {
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 50%;
  background-color: black;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: -16px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1;
}

.arrow-down::before,
.arrow-up::before {
  content: "";
  display: inline-block;
  border: solid white;
  border-width: 0 2px 2px 0;
  padding: 3px;
  transform: rotate(45deg);
  transition: transform 0.3s ease;
}

.arrow-down::before {
  transform: rotate(45deg);
}

.arrow-up::before {
  transform: rotate(-135deg);
}

.student-info {
  margin: 0;
  color: #000;
  font-family: "Montserrat";
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
}
</style>
